import { Popover } from '@mantine/core';
import React, { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Card, { CardDescription, CardFooter, CardFooterDate, CardHeader, CardMenu } from '../../components/Card';
import CreatePageButton from '../../components/CreatePageButton';
import EmptyState from '../../components/EmptyState';
import Error from '../../components/Error';
import Header from '../../components/Header';
import IconPricingPages from '../../components/icons/IconPricingPages';
import Loader from '../../components/Loader';
import Modal, { MODAL_STYLES } from '../../components/Modal';
import PageStatus from '../../components/PageStatus';
import SingleColGrid from '../../components/SingleColGrid';
import { ROUTES } from '../../routes/routes';
import { PricingPage } from '../../types/pricingPage';
import { useAxios } from '../../utils/hooks';
import Preview from './NewPage/Preview';
import SnippetPanel from './NewPage/SnippetPanel';
import { withPageIdParam } from './NewPage/utils';

export default function PricingPages() {
    const { addToast, removeAllToasts } = useToasts();
    const axios = useAxios();
    const [pages, setPages] = useState<PricingPage[]>();
    useEffect(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<PricingPage[]>('/pricing-pages')
            .then((res) => setPages(res.data))
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
    }, [axios, addToast]);

    const handleDeletePage = useCallback(
        ({ id }: PricingPage) =>
            axios.current?.delete(`/pricing-pages/${id}`).then(() => {
                setPages(pages?.filter((page) => page.id !== id));
            }) ?? Promise.resolve(),
        [axios, pages]
    );

    const handleCopyPage = useCallback(
        ({ id }: PricingPage) => {
            addToast('Copying...', {
                appearance: 'info',
            });
            return (
                axios.current
                    ?.post<PricingPage>(`/pricing-pages/${id}/copy`)
                    .then((res) => {
                        setPages([...(pages || []), res.data]);
                        removeAllToasts();
                        addToast('Page copied', {
                            appearance: 'success',
                        });
                    })
                    .catch((err) => {
                        addToast('Something went wrong while copying. Please reload the page and try again.', {
                            appearance: 'error',
                        });
                    }) ?? Promise.resolve()
            );
        },
        [axios, pages, addToast, removeAllToasts]
    );

    return (
        <div className="p-4 w-full text-center">
            <Header>
                <IconPricingPages className="inline w-6 mr-3" /> Pricing Pages
            </Header>
            <div className="p-4">
                {!pages && <Loader full />}
                {pages?.length === 0 && (
                    <EmptyState
                        body={
                            <div className="md:w-1/2 m-auto py-4">
                                <h4 className="text-xl">No Pages Found</h4>
                                <span>You can create a new pricing page by clicking the button below</span>
                                <div className="md:w-48 m-auto mt-6">
                                    <CreatePageButton />
                                </div>
                            </div>
                        }
                    />
                )}
                {pages && pages.length > 0 && (
                    <SingleColGrid>
                        {pages.map((page) => (
                            <PageInfo
                                key={page.id}
                                page={page}
                                onDelete={() => handleDeletePage(page)}
                                onCopy={() => handleCopyPage(page)}
                            />
                        ))}
                    </SingleColGrid>
                )}
            </div>
        </div>
    );
}
function PageInfo(props: { page: PricingPage; onDelete: () => Promise<void>; onCopy: () => Promise<void> }) {
    const history = useHistory();
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { page } = props;

    const handleClickEdit = () => history.push(withPageIdParam(ROUTES.PAGES_NEW, page.id));

    const handleConfirmDelete = () => {
        setIsDeleting(true);
        props.onDelete().catch((err) => {
            setIsDeleting(false);
            setError('Something went wrong. Please try again.');
        });
    };

    const handleDeleteClick = (closeMenu: () => void) => {
        closeMenu();
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
    };

    const handleCopy = () => {
        props.onCopy();
    };

    return (
        <Card>
            <CardHeader onClick={handleClickEdit} status={<PageStatus page={page} />}>
                <div style={{ zoom: '70%' }}>
                    <Preview page={page} centered hideCustomCSS zoom={75} />
                </div>
            </CardHeader>
            <CardDescription
                title={
                    <button
                        onClick={handleClickEdit}
                        className="font-medium text-lg inline-block mb-2 hover:text-blue-400"
                    >
                        {page.name}
                    </button>
                }
            />
            <CardFooter>
                <CardFooterDate date={page.createdAt} />

                {page.status !== 'DRAFT' && (
                    <span className="py-1 text-xs font-regular text-gray-900 mr-1 flex flex-row items-center pl-4 hover:text-gray-600">
                        <svg className="h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                            ></path>
                        </svg>

                        <CopySnippetButton page={page} />
                    </span>
                )}
                <button
                    onClick={handleClickEdit}
                    className="ml-4 relative text-xs font-regular text-gray-900 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1792 1792"
                        className="inline h-5 fill-current mr-1"
                        stroke="currentColor"
                    >
                        <path d="m888 1184 116-116-152-152-116 116v56h96v96h56zm440-720q-16-16-33 1L945 815q-17 17-1 33t33-1l350-350q17-17 1-33zm80 594v190q0 119-84.5 203.5T1120 1536H288q-119 0-203.5-84.5T0 1248V416q0-119 84.5-203.5T288 128h832q63 0 117 25 15 7 18 23 3 17-9 29l-49 49q-14 14-32 8-23-6-45-6H288q-66 0-113 47t-47 113v832q0 66 47 113t113 47h832q66 0 113-47t47-113v-126q0-13 9-22l64-64q15-15 35-7t20 29zm-96-738 288 288-672 672H640V992zm444 132-92 92-288-288 92-92q28-28 68-28t68 28l152 152q28 28 28 68t-28 68z" />
                    </svg>
                    Edit
                </button>
                <CardMenu
                    items={[
                        { title: 'Edit', onClick: handleClickEdit, color: 'GREY' },
                        { title: 'Copy', onClick: handleCopy, color: 'GREY' },
                        { title: 'Delete', onClick: handleDeleteClick, color: 'RED' },
                    ]}
                />
                <ReactModal isOpen={showDeleteModal} style={MODAL_STYLES}>
                    <Modal
                        type="WARN"
                        title="Delete Pricing Page"
                        body={
                            isDeleting ? (
                                <Loader small />
                            ) : (
                                <>
                                    <span>
                                        {props.page.status === 'ACTIVE' ? (
                                            <strong>This Pricing Page is Live! </strong>
                                        ) : (
                                            ''
                                        )}
                                        Are you sure you want to delete this Pricing Page? All of your data will be
                                        permanently removed. This action cannot be undone.
                                    </span>
                                    {error && <Error message={error} />}
                                </>
                            )
                        }
                        confirmButtonText="Delete"
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCloseModal}
                    />
                </ReactModal>
            </CardFooter>
        </Card>
    );
}

function CopySnippetButton(props: { page: PricingPage }) {
    const [isSnippetBoxOpen, setIsSnippetBoxOpen] = useState(false);
    const handleClickCopySnippet = () => {
        setIsSnippetBoxOpen(true);
    };

    return (
        <Popover opened={isSnippetBoxOpen} onChange={setIsSnippetBoxOpen} withArrow>
            <Popover.Target>
                <button onClick={handleClickCopySnippet} className="ml-1 relative">
                    Copy Snippet
                </button>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="w-96">
                    <SnippetPanel page={props.page} />
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
